import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { djangoApi } from "../redux/slices/djangoApi";

const WS_URL =
  process.env.REACT_APP_WS_URL ||
  `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${
    window.location.host
  }/ws/devices/`;

/**
 * Hook that opens a WebSocket to the backend for real-time device status.
 *
 * On receiving a message, it patches the RTK Query cache directly —
 * both the individual getDevice cache and the getBulkStatus cache.
 * This means all components using those queries update instantly
 * with no polling required.
 */
const useDeviceWebSocket = () => {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("[WS] Connected to device status stream");
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === "status_update") {
          const { serial_number, data } = msg;

          // Patch getDevice cache for this specific device
          dispatch(
            djangoApi.util.updateQueryData(
              "getDevice",
              serial_number,
              (draft) => {
                if (data.power_state) draft.power_state = data.power_state;
                if (data.firmware_version)
                  draft.firmware_version = data.firmware_version;
                if (data.runtime) draft.runtime = data.runtime;
                if (data.device_status != null)
                  draft.device_status = data.device_status;
                if (data.last_message) draft.last_message = data.last_message;
              }
            )
          );

          // Patch getBulkStatus cache
          dispatch(
            djangoApi.util.updateQueryData(
              "getBulkStatus",
              undefined,
              (draft) => {
                if (draft.statuses?.[serial_number]) {
                  Object.assign(draft.statuses[serial_number], data);
                }
              }
            )
          );
        } else if (msg.type === "connection_change") {
          const { serial_number, status } = msg;

          // Patch getDevice cache
          dispatch(
            djangoApi.util.updateQueryData(
              "getDevice",
              serial_number,
              (draft) => {
                draft.status = status;
              }
            )
          );

          // Patch getBulkStatus cache
          dispatch(
            djangoApi.util.updateQueryData(
              "getBulkStatus",
              undefined,
              (draft) => {
                if (draft.statuses?.[serial_number]) {
                  const prev = draft.statuses[serial_number].status;
                  draft.statuses[serial_number].status = status;

                  // Update summary counts
                  if (draft.summary && prev !== status) {
                    if (prev === "ONLINE") draft.summary.online--;
                    else draft.summary.offline--;

                    if (status === "ONLINE") draft.summary.online++;
                    else draft.summary.offline++;
                  }
                }
              }
            )
          );
        }
      } catch (err) {
        console.error("[WS] Failed to parse message:", err);
      }
    };

    ws.onclose = () => {
      console.log("[WS] Disconnected, reconnecting in 3s...");
      reconnectTimer.current = setTimeout(connect, 3000);
    };

    ws.onerror = (err) => {
      console.error("[WS] Error:", err);
      ws.close();
    };
  }, [dispatch]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);
};

export default useDeviceWebSocket;
